import { ChangeDetectionStrategy, Component, inject, Signal, signal } from '@angular/core';
import type { FormControlStatus } from '@angular/forms';

import { TranslatePipe } from '@core/i18n-legacy';
import { TellentCurrentUserDataFacade } from '@core/tellent-logic';
import {
  TeamMemberStatus,
  TellentOrgAbilities,
  TellentOrgAbilitiesExtended,
} from '@core/tellent-types';
import {
  ButtonComponent,
  DialogBodyComponent,
  DialogComponent,
  DialogFooterComponent,
  DialogHeaderComponent,
} from '@core/user-interface-legacy';

import { EditMemberPermissionsComponent } from '../edit-member-admin-dialog/edit-member-permissions-access-tab/edit-member-permissions-dialog/edit-member-permissions/edit-member-permissions.component';
import {
  AbilityGroupViewModel,
  SimplifiedMember,
} from '../edit-member-admin-dialog/edit-member-permissions-access-tab/edit-member-permissions-dialog/edit-member-permissions/types';

interface DialogData {
  abilities: Array<TellentOrgAbilities | TellentOrgAbilitiesExtended>;
}

interface DialogResult {
  abilities: Array<TellentOrgAbilities | TellentOrgAbilitiesExtended>;
}

@Component({
  selector: 'tla-give-member-permissions-dialog',
  templateUrl: './give-admin-permissions-dialog.component.html',
  styleUrl: './give-admin-permissions-dialog.component.less',
  imports: [
    DialogHeaderComponent,
    DialogBodyComponent,
    DialogFooterComponent,
    EditMemberPermissionsComponent,
    ButtonComponent,
    TranslatePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GiveAdminPermissionsDialogComponent extends DialogComponent<DialogData, DialogResult> {
  private readonly currentUserFacade = inject(TellentCurrentUserDataFacade);

  public memberData = signal<SimplifiedMember | null>(null);

  public adminData: Signal<SimplifiedMember | null>;

  public state = signal<{
    value: Array<TellentOrgAbilities | TellentOrgAbilitiesExtended>;
    disabled: boolean;
    selectedGroup: AbilityGroupViewModel | null | undefined;
  }>({
    selectedGroup: null,
    value: [],
    disabled: true,
  });

  public setDialogData(): void {
    this.adminData = this.currentUserFacade.organizationMembershipSafe;
    this.memberData.set({
      owner: false,
      status: TeamMemberStatus.NotInvited,
      abilities: this.dialogData.abilities,
      userGuid: 'new_user_guid_placeholder',
    });
  }

  public cancel(): void {
    this.dialogRef.close();
  }

  public save(): void {
    this.dialogRef.close({ abilities: this.state().value });
  }

  public handleValueChange($event: Array<TellentOrgAbilities | TellentOrgAbilitiesExtended>) {
    this.state.set({
      selectedGroup: this.state().selectedGroup,
      value: $event,
      disabled: this.state().disabled,
    });
  }

  public handleStatusChange({
    status,
    pristine,
  }: {
    status: FormControlStatus;
    pristine: boolean;
  }) {
    this.state.set({
      selectedGroup: this.state().selectedGroup,
      value: this.state().value,
      disabled: status === 'DISABLED',
    });
  }

  public handleSelectedGroupChange($event: AbilityGroupViewModel | null | undefined): void {
    this.state.set({
      selectedGroup: $event,
      value: this.state().value,
      disabled: this.state().disabled,
    });
  }
}
